import * as Sentry from "@sentry/react";

const warnedFallbacks = new Set<string>();

/**
 * Report each missing half of the served copy once per session for each
 * workflow, so one mismatched column does not silence the report of another.
 */
function warnDeletedWorkflowStepsFallback(
  half: "column" | "run",
  workflowId: number | null,
  message: string,
) {
  const workflow = String(workflowId ?? "none");
  const key = `${half}:${workflow}`;
  if (warnedFallbacks.has(key)) {
    return;
  }
  warnedFallbacks.add(key);
  Sentry.captureException(new Error(message), {
    level: "warning",
    tags: {
      source: "analytics",
      operation: "deletedWorkflowStepsFallback",
      automationWorkflowId: workflow,
    },
  });
}

/**
 * What the Deterministic Steps tab and its preview say once the workflow behind
 * a run has been deleted in Optro Analytics: the read of the served copy, and
 * the date-free wording that stands in where a half of it is not on the wire.
 *
 * **These strings belong to the backend.** They are authored in
 * `midship/heretic/analytics/copy.py` beside the status copy —
 * `DELETED_WORKFLOW_STEPS_TITLE`, `deleted_workflow_steps_copy`,
 * `DELETED_WORKFLOW_STEP_PREVIEW_TITLE`, `DELETED_WORKFLOW_STEP_PREVIEW_BODY`
 * and `deleted_workflow_preview_header`. That module is the single authoring of
 * these strings for every renderer of them, and a constant declared in a React
 * component would be unreachable to the Python ones.
 *
 * **Nothing here states a date.** The server dates the run in one time zone so
 * every reader of a result sees the same day; a client-side date would be the
 * reader's own zone, and two auditors could read one run as ran on two days.
 *
 * Both halves answer in one object, which the two surfaces take whole rather
 * than picking strings out of, so which of them supplied it is this module's
 * business alone and no component above knows there is a choice.
 */

/**
 * Whether the workflow behind this run still exists, and where it does not, the
 * copy that stands in for the evidence it took with it.
 *
 * A third arm for the question still being out, rather than a nullable that
 * collapses "not deleted" and "not yet known" into one value. The two render
 * differently and must: a surface that drew a step's card while the question
 * was open would be drawing it off a read, which is the one thing this state
 * exists to stop: the card's records are Midship's and outlive the deletion,
 * and reads of its cascade-deleted runs fail as an engine outage does.
 */
export type WorkflowDeletion =
  | { kind: "unsettled" }
  | { kind: "intact" }
  | { kind: "deleted"; copy: DeletedWorkflowSteps };

/** The copy, as the two surfaces render it. */
export interface DeletedWorkflowSteps {
  /** The steps tab's replacement card, which waits on the run's manifest. */
  card: DeletedWorkflowStepsCard;
  /** The preview pane's header, which names the workflow and its fate. */
  previewHeader: string;
  /** The preview pane's title, where a step card would otherwise be drawn. */
  previewTitle: string;
  /** Why there is nothing to preview. */
  previewBody: string;
}

/**
 * The card's copy, or `pending` while the manifest that serves it is still out.
 *
 * Pending is its own arm rather than the fallback wording drawn early: the
 * fallback says less than the served card, and swapping one account of the
 * deletion for another a moment later reads as the pane correcting itself.
 */
export type DeletedWorkflowStepsCard =
  | { kind: "pending" }
  | {
      kind: "ready";
      /** The replacement card's title. */
      title: string;
      /** The card's account of what was lost. */
      body: string;
      /**
       * The card's closing sentence, a paragraph of its own rather than the tail
       * of the one above — the assurance that the result still stands is the
       * part an auditor most needs to read, and it is least likely to be read at
       * the end of the sentence explaining what is gone.
       */
      reassurance: string;
    };

/**
 * The card where the manifest does not serve one: its read failed, found no run
 * or was never issued, or it answered without the copy.
 *
 * The deleted state still has to say something true there, so this names only
 * what the column's status established — that the workflow and its step record
 * are gone — and neither the run's date nor its population, which only the
 * served copy can state for every reader alike.
 */
const DELETED_WORKFLOW_STEPS_CARD_UNAVAILABLE: DeletedWorkflowStepsCard = {
  kind: "ready",
  title: "Step detail is no longer available",
  body: "The workflow that produced this result was deleted in Optro Analytics, and its per-step record was deleted with it.",
  reassurance: "The result in Workpaper Review is unaffected.",
};

/** The column-scoped half of the served copy, off the status payload. */
interface ServedColumnCopy {
  stepPreviewTitle: string;
  stepPreviewBody: string;
  stepPreviewHeader: string;
}

/** The run-scoped half, off the verdict's own run manifest. */
interface ServedRunCopy {
  stepsTitle: string;
  stepsBody: string;
  stepsReassurance: string;
}

/**
 * Where the verdict's run manifest read stands.
 *
 * A read that answered is its own arm, apart from one that failed, found no run
 * or was never issued, because only an answer can break the contract: the
 * manifest serves this copy for every column with a workflow binding, and a
 * `workflow_deleted` status implies one, so an answer carrying none is a
 * mismatch. It is reported only while the status is not being re-read: a
 * cached `workflow_deleted` can outlive a detach during that refetch, and the
 * manifest then answers without copy before the status clears. A workflow
 * detached between two settled reads still answers the same way, and is
 * reported with the mismatches.
 */
export type RunManifestCopy =
  | { kind: "pending" }
  | { kind: "answered"; copy: ServedRunCopy | null }
  | { kind: "unavailable" };

/**
 * The copy for these two surfaces, each half taken on its own.
 *
 * The halves are served separately because they answer at different grains: the
 * preview strings name only the workflow and belong to the column, while the
 * card's body names *this* run and belongs to the manifest. Neither waits on the
 * other — the preview carries no date, so a present column half renders whatever
 * the manifest is doing.
 *
 * A missing column half is a contract mismatch, since the status serves it with
 * the `workflow_deleted` it accompanies, and is reported as one; its stand-in
 * names the workflow by id and never by name, because the only name Midship
 * holds is free text from authoring time and the live lookup that would supply
 * the real one is the call answering `410 Gone` in this very state.
 */
export function servedDeletedWorkflowSteps(
  column: ServedColumnCopy | null | undefined,
  run: RunManifestCopy,
  workflowId: number | null,
  {
    warnOnMissingColumn = true,
    warnOnMissingRun = true,
  }: { warnOnMissingColumn?: boolean; warnOnMissingRun?: boolean } = {},
): DeletedWorkflowSteps {
  if (!column && warnOnMissingColumn) {
    // The status response carries `workflow_deleted` and column-scoped
    // copy together, so a missing preview there is a contract mismatch.
    warnDeletedWorkflowStepsFallback(
      "column",
      workflowId,
      "Using composed deleted workflow steps copy because served column-scoped copy is missing",
    );
  }
  if (run.kind === "answered" && run.copy === null && warnOnMissingRun) {
    warnDeletedWorkflowStepsFallback(
      "run",
      workflowId,
      "Using composed deleted workflow steps copy because the run manifest answered without run-scoped copy",
    );
  }
  return {
    card: deletedWorkflowStepsCard(run),
    previewHeader:
      column?.stepPreviewHeader ??
      (workflowId === null
        ? "Deleted in Optro Analytics"
        : `Workflow ${workflowId} · Deleted in Optro Analytics`),
    previewTitle: column?.stepPreviewTitle ?? "No step preview",
    previewBody:
      column?.stepPreviewBody ??
      "The workflow that produced this result was deleted in Optro Analytics. Its step outputs were deleted with it and cannot be previewed.",
  };
}

function deletedWorkflowStepsCard(
  run: RunManifestCopy,
): DeletedWorkflowStepsCard {
  if (run.kind === "pending") {
    return { kind: "pending" };
  }
  if (run.kind === "unavailable" || run.copy === null) {
    return DELETED_WORKFLOW_STEPS_CARD_UNAVAILABLE;
  }
  return {
    kind: "ready",
    title: run.copy.stepsTitle,
    body: run.copy.stepsBody,
    reassurance: run.copy.stepsReassurance,
  };
}
